import { z } from 'zod';
import { LOCATION_CODE_PATTERN } from '../codes';
import {
  LOCATION_STATUSES,
  type LocationDisplayStatus,
  type LocationStatus,
  type PalletStatus,
} from '../enums';
import { idSchema, optionalText, requiredText } from './common';

export const MAX_GRID_COORDINATE = 999;
/** Límite de ubicaciones por operación de generación (evita errores de tecleo masivos). */
export const MAX_LOCATIONS_PER_GENERATION = 2000;

const gridCoordinate = z
  .number({ error: 'Número obligatorio' })
  .int('Número entero')
  .min(1, 'Mínimo 1')
  .max(MAX_GRID_COORDINATE, `Máximo ${MAX_GRID_COORDINATE}`);

export const zoneInputSchema = z.object({
  code: z
    .string({ error: 'Campo obligatorio' })
    .trim()
    .toUpperCase()
    .min(1, 'Campo obligatorio')
    .max(10)
    .regex(/^[A-Z0-9]+$/, 'Solo letras y números'),
  name: requiredText(100),
  sortOrder: z.number().int().min(0).max(9999).default(0),
  notes: optionalText(500),
});
export type ZoneInput = z.input<typeof zoneInputSchema>;
export type ZoneData = z.output<typeof zoneInputSchema>;

export const locationCodeSchema = z
  .string({ error: 'Campo obligatorio' })
  .trim()
  .toUpperCase()
  .min(1, 'Campo obligatorio')
  .max(30)
  .regex(LOCATION_CODE_PATTERN, 'Solo letras, números, espacio, punto, guion o barra');

export const locationInputSchema = z.object({
  zoneId: idSchema,
  code: locationCodeSchema,
  rowNumber: gridCoordinate,
  positionNumber: gridCoordinate,
  status: z.enum(LOCATION_STATUSES).default('ACTIVE'),
  notes: optionalText(500),
});
export type LocationInput = z.input<typeof locationInputSchema>;
export type LocationData = z.output<typeof locationInputSchema>;

/** Marcadores del patrón de código: {Z} zona, {R} fila, {P} posición. */
export const DEFAULT_LOCATION_CODE_PATTERN = '{Z}-{R}-{P}';

export const generateLocationsSchema = z
  .object({
    zoneId: idSchema,
    rowFrom: gridCoordinate,
    rowTo: gridCoordinate,
    positionFrom: gridCoordinate,
    positionTo: gridCoordinate,
    codePattern: z.string().trim().min(1).max(40).default(DEFAULT_LOCATION_CODE_PATTERN),
    padding: z.number().int().min(1).max(4).default(2),
  })
  .refine((v) => v.rowTo >= v.rowFrom, {
    message: 'La fila final debe ser ≥ la inicial',
    path: ['rowTo'],
  })
  .refine((v) => v.positionTo >= v.positionFrom, {
    message: 'La posición final debe ser ≥ la inicial',
    path: ['positionTo'],
  })
  .refine(
    (v) =>
      (v.rowTo - v.rowFrom + 1) * (v.positionTo - v.positionFrom + 1) <=
      MAX_LOCATIONS_PER_GENERATION,
    {
      message: `Máximo ${MAX_LOCATIONS_PER_GENERATION} ubicaciones por operación`,
      path: ['positionTo'],
    },
  )
  .refine((v) => v.rowFrom === v.rowTo || v.codePattern.includes('{R}'), {
    message: 'Con varias filas el patrón debe incluir {R}',
    path: ['codePattern'],
  })
  .refine((v) => v.positionFrom === v.positionTo || v.codePattern.includes('{P}'), {
    message: 'Con varias posiciones el patrón debe incluir {P}',
    path: ['codePattern'],
  });
export type GenerateLocationsInput = z.input<typeof generateLocationsSchema>;
export type GenerateLocationsData = z.output<typeof generateLocationsSchema>;

/** Construye el código de una ubicación a partir del patrón (compartido por API y vista previa web). */
export function buildLocationCode(
  pattern: string,
  zoneCode: string,
  row: number,
  position: number,
  padding: number,
): string {
  return pattern
    .replaceAll('{Z}', zoneCode)
    .replaceAll('{R}', String(row).padStart(padding, '0'))
    .replaceAll('{P}', String(position).padStart(padding, '0'))
    .toUpperCase();
}

export const listLocationsQuerySchema = z.object({
  zoneId: idSchema.optional(),
  q: z.string().trim().max(50).optional(),
  status: z.enum(LOCATION_STATUSES).optional(),
});
export interface ListLocationsQuery {
  zoneId?: number;
  q?: string;
  status?: LocationStatus;
}

export interface ZoneDto {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
  notes: string | null;
  locationCount: number;
}

export interface LocationDto {
  id: number;
  zoneId: number;
  zoneCode: string;
  code: string;
  rowNumber: number;
  positionNumber: number;
  status: LocationStatus;
  notes: string | null;
  /** Palet que la ocupa ahora mismo (si hay). */
  palletId: number | null;
  palletCode: string | null;
}

export interface GenerateLocationsResult {
  created: number;
  skippedExisting: number;
}

/** Resumen del palet que ocupa una ubicación (mapa). */
export interface MapPalletDto {
  id: number;
  code: string;
  status: PalletStatus;
  clientId: number;
  clientName: string;
  productName: string;
  clientReference: string | null;
  enteredAt: string;
  daysStored: number;
}

export interface MapLocationDto {
  id: number;
  code: string;
  rowNumber: number;
  positionNumber: number;
  status: LocationStatus;
  display: LocationDisplayStatus;
  notes: string | null;
  pallet: MapPalletDto | null;
}

export interface MapZoneDto {
  id: number;
  code: string;
  name: string;
  rows: number;
  positions: number;
  locations: MapLocationDto[];
}

export interface WarehouseMapDto {
  zones: MapZoneDto[];
  generatedAt: string;
}
